import { Component, computed, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { OperatingSiteFormComponent } from './operating-site-form.component';
import { OperatingSiteVM, emptyOperatingSite } from './operational-sites.model';

/**
 * Sezione "Sede Operativa" condivisa dalle form "Dati Specifici".
 *
 * Gestisce:
 * - il flag "la sede legale è anche una sede operativa?" (Sì/No);
 * - la lista di N sedi operative aggiuntive con aggiungi/rimuovi;
 * - il tooltip che invita a indicare tutte le sedi fisiche concordate con
 *   l'ufficio AXA Gestione Fiduciari.
 *
 * Regola: se la sede legale NON è operativa, almeno una sede è obbligatoria.
 * La validazione di riepilogo resta nel form padre (validateOperationalSites);
 * qui vengono mostrati solo gli errori inline.
 */
@Component({
  selector: 'app-operational-sites-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RadioButtonModule,
    TooltipModule,
    OperatingSiteFormComponent,
  ],
  templateUrl: './operational-sites-section.component.html',
})
export class OperationalSitesSectionComponent {
  /** La sede legale è anche una sede operativa. */
  sameAsLegal = model<boolean>(true);
  /** Sedi operative con indirizzo esplicito. */
  sites = model<OperatingSiteVM[]>([]);
  readonly = input<boolean>(false);
  formSubmitted = input<boolean>(false);

  liveRegionMessage = signal<string>('');

  readonly siteTooltip =
    'Indica tutte le sedi operative fisiche concordate con l\'ufficio AXA Gestione Fiduciari.';

  /** Manca la sede obbligatoria: legale non operativa e nessuna sede indicata. */
  missingRequiredSite = computed(
    () => this.formSubmitted() && !this.sameAsLegal() && this.sites().length === 0,
  );

  addSite(): void {
    this.sites.update((s) => [...s, emptyOperatingSite()]);
    this.announce(`Sede operativa ${this.sites().length} aggiunta`);
  }

  removeSite(site: OperatingSiteVM): void {
    this.sites.update((s) => s.filter((x) => x.key !== site.key));
    this.announce('Sede operativa rimossa');
  }

  private announce(message: string): void {
    this.liveRegionMessage.set(message);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);
  }
}
